import Keycloak from "keycloak-js";
import keycloakConfig from "@/config/keycloak";

interface InitOptions {
  onLoad?: "login-required" | "check-sso";
  pkceMethod?: string;
  silentCheckSsoRedirectUri?: string;
  checkLoginIframe?: boolean;
  [key: string]: any;
}

class AuthService {
  private static instance: AuthService | null = null;
  private keycloak: any = null;
  private isInitialized: boolean = false;
  private initPromise: Promise<boolean> | null = null;
  private checkSsoPromise: Promise<boolean> | null = null;

  private constructor() {
    this.initKeycloakInstance();
  }

  private initKeycloakInstance(): void {
    try {
      this.keycloak = new (Keycloak as any)({
        url: keycloakConfig.KEYCLOAK_URL,
        realm: keycloakConfig.KEYCLOAK_REALM,
        clientId: keycloakConfig.KEYCLOAK_CLIENT_ID,
      });
    } catch (error) {
      console.error("❌ Failed to create Keycloak instance:", error);
      throw error;
    }
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public async init(options?: InitOptions): Promise<boolean> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise(async (resolve) => {
      try {
        const defaultOptions: InitOptions = {
          onLoad: "login-required",
          responseType: "code",
          scope: "openid",
          redirectUri: keycloakConfig.KEYCLOAK_REDIRECT_URL,
          ...options,
        };

        const result = await this.keycloak.init(defaultOptions);

        let authenticated = false;
        if (typeof result === "boolean") {
          authenticated = result;
        } else if (result && typeof result === "object") {
          authenticated = result.authenticated || false;
        }

        if (this.keycloak.authenticated !== undefined) {
          authenticated = this.keycloak.authenticated;
        }

        this.isInitialized = true;

        if (authenticated) {
          this.storeTokens();
        }

        resolve(authenticated);
      } catch (error) {
        console.error("❌ Authentication initialization failed:", error);
        this.isInitialized = false;
        resolve(false);
      } finally {
        this.initPromise = null;
      }
    });

    return this.initPromise;
  }

  public async checkSSO(): Promise<boolean> {
    if (this.checkSsoPromise) {
      return this.checkSsoPromise;
    }

    this.checkSsoPromise = new Promise(async (resolve) => {
      try {
        if (!this.keycloak) {
          this.initKeycloakInstance();
        }

        const options: InitOptions = {
          onLoad: "check-sso",
          silentCheckSsoRedirectUri:
            window.location.origin + "/silent-check-sso.html",
          checkLoginIframe: false,
          enableLogging: true,
          responseMode: "query",
          scope: "openid",
          redirectUri: keycloakConfig.KEYCLOAK_REDIRECT_URL,
          adapter: "default",
        };

        const result = await this.keycloak.init(options);

        let authenticated = false;
        if (typeof result === "boolean") {
          authenticated = result;
        } else if (result && typeof result === "object") {
          authenticated = result.authenticated || false;
        }

        if (this.keycloak.authenticated !== undefined) {
          authenticated = this.keycloak.authenticated;
        }

        if (authenticated) {
          this.storeTokens();
        }

        resolve(authenticated);
      } catch (error) {
        console.error("❌ SSO check failed:", error);
        resolve(false);
      } finally {
        this.checkSsoPromise = null;
      }
    });

    return this.checkSsoPromise;
  }

  public login(options?: any): void {
    if (!this.keycloak) {
      this.manualRedirectToLogin();
      return;
    }

    const loginOptions = {
      redirectUri: keycloakConfig.KEYCLOAK_REDIRECT_URL,
      scope: "openid",
      responseType: "code",
      ...options,
    };

    this.keycloak.login(loginOptions);
  }

  private manualRedirectToLogin(): void {
    const redirectUri = encodeURIComponent(
      keycloakConfig.KEYCLOAK_REDIRECT_URL || window.location.origin
    );
    const authUrl = `${keycloakConfig.KEYCLOAK_URL}/realms/${keycloakConfig.KEYCLOAK_REALM}/protocol/openid-connect/auth?client_id=${keycloakConfig.KEYCLOAK_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=code&scope=openid`;
    window.location.href = authUrl;
  }

  private storeTokens(): void {
    if (this.keycloak.token) {
      localStorage.setItem("token", JSON.stringify(this.keycloak.token));
      localStorage.setItem("user", JSON.stringify(this.keycloak.tokenParsed));

      localStorage.setItem("kc_token", this.keycloak.token);
      localStorage.setItem("kc_refresh_token", this.keycloak.refreshToken || "");
      localStorage.setItem(
        "kc_token_expiry",
        Date.now() + (this.keycloak.tokenParsed?.exp || 0) * 1000 + ""
      );
    }
  }

  public getToken(): string | null {
    return this.keycloak?.token || localStorage.getItem("kc_token");
  }

  public logout(options?: {
    redirectUri?: string;
    idTokenHint?: string;
    postLogoutRedirectUri?: string;
  }): void {
    localStorage.removeItem("kc_token");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("kc_refresh_token");
    localStorage.removeItem("kc_token_expiry");

    const redirectUri =
      options?.redirectUri ||
      options?.postLogoutRedirectUri ||
      window.location.origin;

    const idTokenHint =
      options?.idTokenHint || this.keycloak?.idToken || this.getToken();

    let logoutUrl = `${keycloakConfig.KEYCLOAK_URL}/realms/${keycloakConfig.KEYCLOAK_REALM}/protocol/openid-connect/logout`;

    const params = new URLSearchParams();

    if (idTokenHint) {
      params.append("id_token_hint", idTokenHint);
    }

    if (redirectUri) {
      params.append("redirect_uri", encodeURIComponent(redirectUri));
    }

    params.append("client_id", keycloakConfig.KEYCLOAK_CLIENT_ID);

    const queryString = params.toString();
    if (queryString) {
      logoutUrl += `?${queryString}`;
    }

    window.location.href = logoutUrl;
  }

  /**
   * Direct login method that mimics your working URL
   */
  public directLogin(): void {
    const redirectUri = encodeURIComponent(keycloakConfig.KEYCLOAK_REDIRECT_URL);
    const authUrl = `${keycloakConfig.KEYCLOAK_URL}/realms/${keycloakConfig.KEYCLOAK_REALM}/protocol/openid-connect/auth?client_id=${keycloakConfig.KEYCLOAK_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=code&scope=openid`;
    window.location.href = authUrl;
  }
}

const authService = AuthService.getInstance();
export default authService;
export { AuthService };

